import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import type { AccountState } from './accountState';
import { youBadge } from './accountState';
import { V4AccountDropdown } from './V4AccountDropdown';

/**
 * THE ONE desktop account avatar (v4 QA rows 2-5, 8, 10).
 *
 * The v4 rebuild only ever wired the in-place account dropdown into the
 * HOMEPAGE. Every other desktop surface re-decided the avatar for itself and
 * they diverged: the board fell back to the legacy `HeaderAccountMenu` (which
 * lists "My people"), the guide NAVIGATED the avatar to the full-page `/you`,
 * and calendar + quiz never got a v4 door at all. That is the exact "three
 * separate places that each decided this for themselves" the journeys spec
 * (rule 4) exists to prevent. This component is that one place.
 *
 * Two renderings, chosen by `state.signedIn`:
 *
 *   SIGNED IN — the avatar shows the account's INITIALS and opens
 *     `V4AccountDropdown` IN PLACE (menu semantics; the host owns close-on-Esc /
 *     close-on-outside-click). Same dropdown the homepage already shipped, so
 *     every surface now shows the same thing.
 *   ANON — a person glyph + the You badge (dot / count). The avatar is a DOOR:
 *     it calls `onAnonClick`, which differs by surface and ONLY by surface — the
 *     homepage opens the sign-in modal, the board opens the capture sheet. That
 *     is the one intended per-surface difference, so it is a prop, not a fork.
 *
 * `testIdPrefix` keeps each surface's existing test hooks (`v4-home-*`,
 * `v4-board-*`) stable through the consolidation.
 */

const Slot = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const Avatar = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  background: ${({ theme }) => theme.color.cream};
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 600;
  color: hsl(var(--foreground));
  cursor: pointer;
  flex-shrink: 0;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 2px;
  }
`;

/* The same visual primitive when a signed-in mobile public-guide avatar is a
   navigation link rather than an in-place desktop account menu. Keep this
   separate from `Avatar` so the semantics are correct: it is a link to /you,
   not a button that happens to navigate. */
const AvatarLink = styled.a`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  background: ${({ theme }) => theme.color.cream};
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 600;
  color: hsl(var(--foreground));
  cursor: pointer;
  flex-shrink: 0;
  text-decoration: none;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 2px;
  }
`;

const Dot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: ${({ theme }) => theme.color.errorRed};
  border: 2px solid ${({ theme }) => theme.color.creamLight};
`;

const CountBadge = styled(Dot)`
  min-width: 17px;
  height: 17px;
  top: -5px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 10.5px;
  font-weight: 600;
  color: #ffffff;
  padding: 0 3px;
`;

/**
 * Up to two initials for the avatar. Mirrors HeaderAccountMenu's source
 * priority (displayName, then email) but yields "KR" for a multi-word name —
 * first + last initial — and "K" for a single word or an email. Returns null
 * when there is no identity at all (anon), so the caller renders a person icon
 * rather than an empty disc.
 */
export function avatarInitials(
  displayName?: string | null,
  email?: string | null,
): string | null {
  const name = (displayName ?? '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }
  const mail = (email ?? '').trim();
  if (mail) return mail[0].toUpperCase();
  return null;
}

/** Neutral silhouette for an avatar with no identity (anon on desktop). */
const PersonIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export interface V4AccountAvatarProps {
  state: AccountState;
  /**
   * The ANON avatar's click — the one deliberate per-surface difference. The
   * homepage wires the sign-in modal; the board wires the capture sheet. A
   * signed-in avatar ignores this and opens its dropdown in place.
   */
  onAnonClick: () => void;
  /** The anon avatar's accessible label ("Your account" / "Save your boards"). */
  anonAriaLabel?: string;
  /** Keeps each surface's existing test hooks stable ("v4-home", "v4-board"). */
  testIdPrefix?: string;
  /**
   * Optional signed-in destination. Used only by a mobile public guide, whose
   * avatar is the route back into the app's existing You tab; all other hosts
   * omit it and retain the in-place account dropdown.
   */
  signedInHref?: string;
}

export const V4AccountAvatar: React.FC<V4AccountAvatarProps> = ({
  state,
  onAnonClick,
  anonAriaLabel = 'Your account',
  testIdPrefix = 'v4-account',
  signedInHref,
}) => {
  const badge = youBadge(state);
  // The signed-in account dropdown. Anchored to the Slot; this component owns
  // close-on-Esc / close-on-outside-click (mirrors the homepage host).
  const [accountOpen, setAccountOpen] = useState(false);
  const slotRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!accountOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (slotRef.current && !slotRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAccountOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountOpen]);

  // Identity is the glyph: initials for a signed-in account, a person icon when
  // there is none (anon). Never a bare bullet (QA #6).
  const initials = state.signedIn
    ? avatarInitials(state.displayName, state.accountEmail)
    : null;

  return (
    <Slot ref={slotRef} data-testid={`${testIdPrefix}-avatar`}>
      {state.signedIn && signedInHref ? (
        <AvatarLink href={signedInHref} aria-label="Go to You tab">
          {initials ? (
            <span aria-hidden="true">{initials}</span>
          ) : (
            <span data-testid={`${testIdPrefix}-person-icon`} style={{ display: 'inline-flex' }}>
              <PersonIcon />
            </span>
          )}
          {badge.kind === 'dot' ? <Dot data-testid={`${testIdPrefix}-dot`} /> : null}
          {badge.kind === 'count' ? (
            <CountBadge data-testid={`${testIdPrefix}-count`}>{badge.count}</CountBadge>
          ) : null}
        </AvatarLink>
      ) : (
        <Avatar
          type="button"
          aria-label={state.signedIn ? 'Your account' : anonAriaLabel}
          // A signed-in avatar opens its dropdown IN PLACE (menu semantics). The
          // anon avatar is the door (onAnonClick) — sign-in modal or capture sheet.
          aria-haspopup={state.signedIn ? 'menu' : undefined}
          aria-expanded={state.signedIn ? accountOpen : undefined}
          onClick={() =>
            state.signedIn ? setAccountOpen((v) => !v) : onAnonClick()
          }
        >
          {initials ? (
            <span aria-hidden="true">{initials}</span>
          ) : (
            <span data-testid={`${testIdPrefix}-person-icon`} style={{ display: 'inline-flex' }}>
              <PersonIcon />
            </span>
          )}
          {badge.kind === 'dot' ? <Dot data-testid={`${testIdPrefix}-dot`} /> : null}
          {badge.kind === 'count' ? (
            <CountBadge data-testid={`${testIdPrefix}-count`}>{badge.count}</CountBadge>
          ) : null}
        </Avatar>
      )}
      {state.signedIn && !signedInHref && accountOpen ? (
        <V4AccountDropdown state={state} onClose={() => setAccountOpen(false)} />
      ) : null}
    </Slot>
  );
};

export default V4AccountAvatar;
